<!--
  @component Navbar.DrawerToggle

  A polymorphic toggle component for the navbar drawer. It handles the open/close state of the drawer
  and provides visual feedback. This component is typically used to control the visibility of a sidebar drawer.

  @prop as - The HTML element to render (default: 'button')
  @prop css - Custom CSS styles to apply to the toggle
  @prop openLabel - Accessible label for the open state (for translations)
  @prop closeLabel - Accessible label for the close state (for translations)

  Example:
  <Navbar.DrawerToggle openLabel="Open sidebar" closeLabel="Close sidebar">
    <Icon name="menu" />
  </Navbar.DrawerToggle>
-->

<script lang="ts">
  import { getContext } from 'svelte';
  import type { NavbarStore } from '../navbar-store.svelte';
  import { visuallyHidden } from '@agapi/ui/panda/patterns';
  import type { NavbarDrawerToggleProps } from '../navbar.types';

  const {
    children,
    css: cssProp,
    as = 'button',
    openLabel = 'Open drawer',
    closeLabel = 'Close drawer',
    ...props
  }: NavbarDrawerToggleProps = $props();

  const navbarStore = getContext<NavbarStore>('navbar');

  const onclick = () => {
    navbarStore.toggleDrawer();
  };

  const label = $derived(navbarStore.isDrawerOpen ? closeLabel : openLabel);
</script>

<svelte:element
  this={as}
  {...props}
  {onclick}
  aria-label={label}
  aria-expanded={navbarStore.isDrawerOpen}
  aria-controls="navbar-drawer"
  data-type="drawer-toggle"
  role={as === 'button' ? undefined : 'button'}
  tabindex={props.tabindex ?? 0}
>
  {#if children}
    {@render children()}
  {/if}
  <span class={visuallyHidden()}>{label}</span>
</svelte:element>
